import * as Sentry from '@sentry/react';
import { type ComponentType, lazy } from 'react';

const RELOAD_FLAG = 'ix_chunk_reload_attempted';
const PENDING_RELOAD_TIMEOUT_MS = 10_000;
// The flag stores the build that already reloaded: at most one reload per deploy.
// A boolean stayed set after a successful reload and blocked recovery from the next
// deploy's stale chunks (ROSE-CLIENT-BACKOFFICE-4); keying it to the build lets a new
// deploy reload once, while a chunk still missing after reloading into the same build
// (partial upload, cached index.html) shows the error screen instead of looping.
// The entry script's hashed URL identifies the build; index.html uses the same key.
export function currentBuildId(): string {
    return (
        document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.src ??
        ''
    );
}

function reloadAlreadyAttemptedForBuild(): boolean {
    return sessionStorage.getItem(RELOAD_FLAG) === currentBuildId();
}

const CHUNK_ERROR_PATTERN =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

export function isChunkLoadError(error: unknown): boolean {
    return error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);
}

// biome-ignore lint/suspicious/noExplicitAny: mirrors React.lazy's constraint; a prop-taking component is not a ComponentType<unknown>.
export function lazyWithRetry<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
) {
    return lazy(async () => {
        try {
            const module = await factory();
            sessionStorage.removeItem(RELOAD_FLAG);
            return module;
        } catch (err) {
            // index.html already started a reload for this chunk's failed preload: the
            // build flag is set, but this is not a failure after reloading. If the reload
            // never happens (an unsaved-changes prompt cancelled it), surface the error.
            if (isChunkLoadError(err) && document.documentElement.dataset.chunkReloadPending) {
                return new Promise<{ default: T }>((_, reject) => {
                    setTimeout(() => reject(err), PENDING_RELOAD_TIMEOUT_MS);
                });
            }
            if (isChunkLoadError(err) && !reloadAlreadyAttemptedForBuild()) {
                sessionStorage.setItem(RELOAD_FLAG, currentBuildId());
                Sentry.captureMessage('chunk_load_error_auto_reload', {
                    level: 'warning',
                    tags: { errorType: 'chunk_load_error', recovery: 'auto_reload' },
                    extra: { message: err instanceof Error ? err.message : String(err) },
                });
                window.location.reload();
                return new Promise<{ default: T }>(() => {});
            }
            throw err;
        }
    });
}
